// NOTICE: KAPTION-INTEGRATION
// Store module for managing dashboard app preloading
// Maximum of 4 apps can be preloaded (3 custom + Kaption Copilot)

const MAX_PRELOADED_APPS = 4;

export const state = {
  // Map of app ID to preload state
  // { appId: { status: 'loading' | 'ready' | 'error', conversationId: number } }
  preloadedApps: {},
  // Current conversation ID for context
  currentConversationId: null,
  // Whether preloading is enabled
  preloadingEnabled: true,
};

export const getters = {
  isAppPreloaded(_state) {
    return appId => {
      const app = _state.preloadedApps[appId];
      return (
        app?.status === 'ready' &&
        app?.conversationId === _state.currentConversationId
      );
    };
  },
  getPreloadedApps(_state) {
    return _state.preloadedApps;
  },
  getPreloadStatus(_state) {
    return appId => _state.preloadedApps[appId]?.status;
  },
  getCurrentConversationId(_state) {
    return _state.currentConversationId;
  },
  isPreloadingEnabled(_state) {
    return _state.preloadingEnabled;
  },
  getPreloadedAppIds(_state) {
    return Object.keys(_state.preloadedApps);
  },
};

export const mutations = {
  SET_PRELOAD_STATUS(_state, { appId, status, conversationId }) {
    _state.preloadedApps = {
      ..._state.preloadedApps,
      [appId]: { status, conversationId },
    };
  },
  CLEAR_PRELOADED_APP(_state, appId) {
    const { [appId]: _, ...rest } = _state.preloadedApps;
    _state.preloadedApps = rest;
  },
  CLEAR_ALL_PRELOADED_APPS(_state) {
    _state.preloadedApps = {};
  },
  SET_CURRENT_CONVERSATION_ID(_state, conversationId) {
    _state.currentConversationId = conversationId;
  },
  SET_PRELOADING_ENABLED(_state, enabled) {
    _state.preloadingEnabled = enabled;
  },
};

export const actions = {
  // Start preloading an app
  startPreload({ commit, state: moduleState }, { appId, conversationId }) {
    // Check if we've hit the max limit
    const preloadedCount = Object.keys(moduleState.preloadedApps).length;
    if (
      preloadedCount >= MAX_PRELOADED_APPS &&
      !moduleState.preloadedApps[appId]
    ) {
      return false;
    }

    commit('SET_PRELOAD_STATUS', {
      appId,
      status: 'loading',
      conversationId,
    });
    return true;
  },

  // Mark an app as ready (preloaded successfully)
  markPreloadReady({ commit, state: moduleState }, { appId }) {
    const app = moduleState.preloadedApps[appId];
    if (app) {
      commit('SET_PRELOAD_STATUS', {
        appId,
        status: 'ready',
        conversationId: app.conversationId,
      });
    }
  },

  // Mark an app as failed to preload
  markPreloadError({ commit, state: moduleState }, { appId }) {
    const app = moduleState.preloadedApps[appId];
    if (app) {
      commit('SET_PRELOAD_STATUS', {
        appId,
        status: 'error',
        conversationId: app.conversationId,
      });
    }
  },

  // Clear a specific preloaded app
  clearPreloadedApp({ commit }, { appId }) {
    commit('CLEAR_PRELOADED_APP', appId);
  },

  // Clear all preloaded apps (e.g., on conversation change)
  clearAllPreloadedApps({ commit }) {
    commit('CLEAR_ALL_PRELOADED_APPS');
  },

  // Update current conversation context
  setCurrentConversation(
    { commit, dispatch, state: moduleState },
    { conversationId }
  ) {
    // If conversation changed, clear stale preloaded apps
    if (
      moduleState.currentConversationId &&
      moduleState.currentConversationId !== conversationId
    ) {
      dispatch('clearAllPreloadedApps');
    }
    commit('SET_CURRENT_CONVERSATION_ID', conversationId);
  },

  // Toggle preloading feature
  setPreloadingEnabled({ commit }, enabled) {
    commit('SET_PRELOADING_ENABLED', enabled);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
